<script setup lang="ts">
/**
 * Directors' Circular Resolution (Change Business Address) — M32017, Section 47(3).
 * · sole / multiple director + Constitution / Articles / Third Schedule
 * · (1) 사업장 주소 변경(from old → new)  (2) records a~k 를 신주소로 이전 통지
 */
import { computed, inject } from 'vue';
import { CHANGE_BUSINESS_ADDRESS_DOC_CTX } from './changeBusinessAddressContext';
import { RECORD_ITEMS } from './changeBusinessAddressFields';
import ChangeBusinessAddressDocVarField from './ChangeBusinessAddressDocVarField.vue';
import ChangeBusinessAddressSigners from './parts/ChangeBusinessAddressSigners.vue';

const ctx = inject(CHANGE_BUSINESS_ADDRESS_DOC_CTX)!;
const isMultiple = computed(() => ctx.flag('is_multiple_director_company'));
const basis = computed(() => ctx.choiceOf('resolution_basis') || 'constitution');

function cycleBasis() {
  const next = basis.value === 'constitution' ? 'articles' : basis.value === 'articles' ? 'third_schedule' : 'constitution';
  ctx.setChoice('resolution_basis', next);
}
</script>

<template>
  <div class="cba-doc">
    <div class="doc-head">
      <div class="doc-meta">Company Registration No: <ChangeBusinessAddressDocVarField field-key="company_registration_no" /></div>
      <div class="doc-company"><ChangeBusinessAddressDocVarField field-key="company_name" /></div>
      <div class="doc-meta italic">(Incorporated in Malaysia)</div>
    </div>

    <div class="dcr-title">DIRECTORS' CIRCULAR RESOLUTION</div>

    <p class="doc-p">
      <span
        class="doc-toggle"
        title="sole / multiple director"
        @click="ctx.toggleFlag('is_multiple_director_company')"
      >{{ isMultiple ? 'We, the undersigned, being the directors' : 'I, the undersigned, being the sole director' }}</span>
      of the above-mentioned Company for the time being, do resolve the following in writing pursuant to
      <template v-if="basis === 'third_schedule'">
        <span class="doc-toggle" title="resolution basis" @click="cycleBasis">Paragraph 15 of the Third Schedule of the Companies Act 2016</span>.
      </template>
      <template v-else>
        <span class="doc-toggle" title="resolution basis" @click="cycleBasis">{{ basis === 'articles' ? 'Article' : 'Clause' }}</span>
        <ChangeBusinessAddressDocVarField field-key="constitution_clause_number" />
        <template v-if="basis === 'articles'"> of the Company's Constitution (Articles of Association)</template>
        <template v-else> of the Company's Constitution</template>.
      </template>
    </p>

    <p class="doc-sec-title">(1) CHANGE OF BUSINESS ADDRESS</p>
    <p class="doc-p">
      THAT the situation of the Company's business address be changed from
      <ChangeBusinessAddressDocVarField field-key="old_business_address" />
      to the following address: -
    </p>
    <p class="doc-addr">
      <ChangeBusinessAddressDocVarField field-key="address_line_1" /><br />
      <ChangeBusinessAddressDocVarField field-key="address_line_2" /><br />
      <ChangeBusinessAddressDocVarField field-key="address_line_3" /><br />
      <ChangeBusinessAddressDocVarField field-key="postcode" /> <ChangeBusinessAddressDocVarField field-key="city" /><br />
      <ChangeBusinessAddressDocVarField field-key="state" />, <ChangeBusinessAddressDocVarField field-key="country" />
    </p>

    <p class="doc-sec-title">(2) NOTIFICATION OF CHANGE IN THE ADDRESS WHERE REGISTERS OR RECORDS ARE KEPT</p>
    <p class="doc-p">
      THAT the following records be moved from
      <ChangeBusinessAddressDocVarField field-key="old_business_address" />
      to
      <ChangeBusinessAddressDocVarField field-key="address_line_1" />,
      <ChangeBusinessAddressDocVarField field-key="address_line_2" />,
      <ChangeBusinessAddressDocVarField field-key="address_line_3" />,
      <ChangeBusinessAddressDocVarField field-key="postcode" /> <ChangeBusinessAddressDocVarField field-key="city" />,
      <ChangeBusinessAddressDocVarField field-key="state" />, <ChangeBusinessAddressDocVarField field-key="country" />
      with immediate effect: -
    </p>
    <ul class="rec-list">
      <li
        v-for="r in RECORD_ITEMS" :key="r.key"
        class="rec-item doc-toggle" :class="{ off: !ctx.flag(r.key) }"
        :title="ctx.flag(r.key) ? 'included — click to exclude' : 'excluded — click to include'"
        @click="ctx.toggleFlag(r.key)"
      >
        <span class="rec-mark">{{ ctx.flag(r.key) ? '☑' : '☐' }}</span>
        <span class="rec-tx">{{ r.text }} ;</span>
      </li>
    </ul>

    <p class="doc-p">
      THAT the Secretary of the Company be and is hereby authorised to lodge the necessary notification to the
      Companies Commission of Malaysia (CCM) accordingly.
    </p>

    <p class="doc-lbl-title">BOARD OF DIRECTORS</p>
    <ChangeBusinessAddressSigners var-key="dcr" board-label="BOARD OF DIRECTORS" />
    <p class="doc-p" style="margin-top:14px"><b>Date:</b> <ChangeBusinessAddressDocVarField field-key="dcr_date" /></p>
  </div>
</template>

<style src="./changeBusinessAddressDoc.css"></style>
